// frontend/src/services/api.js
const API_BASE_URL = 'http://layou-main.test/backend/api';

/**
 * Enhanced API Service for Layou Adventures
 * Includes error handling, caching, and retry logic
 */

// API call wrapper with enhanced error handling
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, defaultOptions);
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'API request failed');
    }

    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// ============================================
// TOURS API
// ============================================
export const toursAPI = {
  getAll: () => apiCall('/tours/'),

  getFeatured: () => apiCall('/tours/?featured=1'),

  getBySlug: (slug) => apiCall(`/tours/?slug=${slug}`),

  getAvailability: (tourId, month = null) => {
    const query = month ? `?tour_id=${tourId}&month=${month}` : `?tour_id=${tourId}`;
    return apiCall(`/tours/availability.php${query}`);
  },
};

// ============================================
// BOOKINGS API
// ============================================

export const bookingsAPI = {
  create: (bookingData) => apiCall('/bookings/create.php', {
    method: 'POST',
    body: JSON.stringify(bookingData),
  }),

  get: (reference) => apiCall(`/bookings/get.php?reference=${reference}`),

  getStatus: (reference) => apiCall(`/bookings/status.php?reference=${reference}`),

  signWaiver: (waiverData) => apiCall('/bookings/waiver.php', {
    method: 'POST',
    body: JSON.stringify(waiverData),
  }),

  sendWaiverEmails: (bookingReference) => apiCall('/bookings/send-waiver-emails.php', {
    method: 'POST',
    body: JSON.stringify({ booking_reference: bookingReference }),
  }),

  getWaiverByToken: (token) => apiCall(`/bookings/get-waiver.php?token=${token}`),

  signWaiverByToken: (token, fullName) => apiCall('/bookings/waiver.php', {
    method: 'POST',
    body: JSON.stringify({
      waiver_token: token,
      full_name: fullName
    }),
  }),

  // ============================================
  // NEW PAYMENT METHODS - STRIPE INTEGRATION
  // ============================================

  /**
   * Create a Stripe payment intent for a booking
   * @param {Object} data - Must contain booking_reference
   * @returns {Promise} Payment intent with client_secret
   */
  createPaymentIntent: (data) => apiCall('/bookings/payment.php?action=intent', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  /**
   * Confirm payment after Stripe processes it
   * @param {Object} data - Must contain booking_reference and payment_intent_id
   * @returns {Promise} Confirmation response
   */
  confirmPayment: (data) => apiCall('/bookings/payment.php?action=confirm', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // ============================================
  // TICKET GENERATION & DOWNLOAD
  // ============================================

  verifyTicket: (reference) =>
    apiCall(`/bookings/verify-ticket.php?reference=${reference}`),

  /**
   * Download ticket PDF for a booking
   * @param {string} reference - Booking reference (e.g., LA-20241220-ABC123)
   * @param {number|null} participantId - Optional: specific participant ID
   */
  downloadTicket: (reference, participantId = null) => {
    const url = participantId
      ? `${API_BASE_URL}/bookings/generate-ticket.php?reference=${reference}&participant_id=${participantId}`
      : `${API_BASE_URL}/bookings/generate-ticket.php?reference=${reference}`;

    // Open in new tab for download
    window.open(url, '_blank');
  },

  /**
   * Get ticket download link (without opening)
   * @param {string} reference - Booking reference
   * @param {number|null} participantId - Optional: specific participant ID
   * @returns {string} Direct download URL
   */
  getTicketUrl: (reference, participantId = null) => {
    return participantId
      ? `${API_BASE_URL}/bookings/generate-ticket.php?reference=${reference}&participant_id=${participantId}`
      : `${API_BASE_URL}/bookings/generate-ticket.php?reference=${reference}`;
  },
};

// ============================================
// BLOG API
// ============================================
export const blogAPI = {
  getPosts: (page = 1, limit = 10, category = null) => {
    let query = `?page=${page}&limit=${limit}`;
    if (category) query += `&category=${encodeURIComponent(category)}`;
    return apiCall(`/blog/${query}`);
  },

  getPost: (slug) => apiCall(`/blog/single.php?slug=${slug}`),

  getCategories: () => apiCall('/blog/categories.php'),

  getFeatured: (limit = 2) => apiCall(`/blog/?featured=1&limit=${limit}`),
};

// ============================================
// CONTENT API
// ============================================
export const contentAPI = {
  getGallery: (category = null, featured = false) => {
    let query = '';
    if (category) query += `?category=${category}`;
    if (featured) query += `${query ? '&' : '?'}featured=1`;
    return apiCall(`/gallery/${query}`);
  },

  getTestimonials: (featured = false) => {
    const query = featured ? '?featured=1' : '';
    return apiCall(`/testimonials/${query}`);
  },

  getSliders: () => apiCall('/sliders/'),

  getPageContent: (pageSlug) => apiCall(`/content/page.php?page_slug=${pageSlug}`),

  getWaiverContent: () => apiCall('/content/waiver.php'),

};

// ============================================
// CONTACT API
// ============================================
export const contactAPI = {
  submit: (contactData) => apiCall('/contact/submit.php', {
    method: 'POST',
    body: JSON.stringify(contactData),
  }),
};

// ============================================
// SETTINGS API
// ============================================
export const settingsAPI = {
  get: () => apiCall('/settings/'),
  getPublic: () => apiCall('/settings/'),
  getStripeKey: () => apiCall('/settings/stripe-key.php'),
};

// Export everything as default
const API = {
  tours: toursAPI,
  bookings: bookingsAPI,
  blog: blogAPI,
  content: contentAPI,
  contact: contactAPI,
  settings: settingsAPI,
};

export default API;